<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { submitCognitoExchangeForm } from 'shared/helpers/cognitoExchange';
export default {
  props: {
    showAccountModal: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      search: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      availableInstances: 'getAvailableInstances',
    }),

    filteredAccounts() {
      if (!this.search) return this.currentUser.accounts;

      return this.currentUser.accounts.filter(account =>
        account.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },

    filteredOtherInstances() {
      if (!this.search) return this.availableInstances;
      const term = this.search.toLowerCase();
      return this.availableInstances.filter(instance =>
        (instance.accountName || '').toLowerCase().includes(term)
      );
    },
  },
  watch: {
    showAccountModal(newVal) {
      if (newVal) {
        this.search = '';
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
  },
  methods: {
    onChangeAccount(accountId) {
      const accountUrl = `/app/accounts/${accountId}/dashboard`;
      window.location.href = accountUrl;
    },

    selectAccount() {
      if (this.filteredAccounts.length === 1) {
        const accountToSelect = this.filteredAccounts[0];
        this.onChangeAccount(accountToSelect.id);
      }
    },
    onClose() {
      this.$emit('closeAccountModal');
      this.search = '';
    },

    async onChangeInstance(instance) {
      const host = (instance.instanceHost || '').replace(/\/$/, '');
      if (!host) return;

      const normalizedHost = /^https?:\/\//i.test(host)
        ? host
        : `https://${host}`;

      let targetOrigin;
      try {
        targetOrigin = new URL(normalizedHost).origin;
      } catch {
        useAlert(this.$t('SIDEBAR_ITEMS.INVALID_INSTANCE_URL'));
        return;
      }

      // Try to obtain the stored Cognito ID token from the current instance
      // and hand it off to the target instance via a cross-origin form POST.
      // If anything fails (no token, expired, network), fall back to the
      // legacy redirect-to-login flow so the user still gets to the target.
      let idToken = null;
      try {
        const { data } = await axios.get('/auth/cognito/id_token');
        idToken = data?.id_token;
      } catch (e) {
        idToken = null;
      }

      if (idToken) {
        submitCognitoExchangeForm(targetOrigin, {
          idToken,
          targetAccountId: instance.accountId,
        });
        return;
      }

      const fallbackUrl = new URL(`${targetOrigin}/app/login`);
      if (this.currentUser?.email) {
        fallbackUrl.searchParams.set('email', this.currentUser.email);
      }
      window.location.href = fallbackUrl.toString();
    },
  },
};
</script>

<template>
  <woot-modal :show="showAccountModal" :on-close="onClose">
    <woot-modal-header
      :header-title="$t('SIDEBAR_ITEMS.CHANGE_ACCOUNTS')"
      :header-content="$t('SIDEBAR_ITEMS.SELECTOR_SUBTITLE')"
    />
    <div class="px-8 py-4">
      <input
        ref="searchInput"
        v-model="search"
        type="text"
        autofocus
        class="emoji-search--input focus:box-shadow-blue dark:focus:box-shadow-dark !h-8 !text-sm"
        :placeholder="$t('CREATE_ACCOUNT.SEARCH_ACCOUNT')"
        @keyup.enter="selectAccount"
      />
      <div class="overflow-auto h-[60dvh]">
        <div
          v-for="account in filteredAccounts"
          :id="`account-${account.id}`"
          :key="account.id"
          class="pt-0 pb-0"
        >
          <button
            class="flex items-center justify-between w-full px-3 py-2 rounded-lg cursor-pointer expanded clear link hover:underline hover:bg-slate-25 dark:hover:bg-slate-900"
            @click="onChangeAccount(account.id)"
          >
            <span class="w-full cursor-pointer">
              <div
                :for="account.name"
                class="text-left rtl:text-right grid grid-cols-[1fr_auto_15px] gap-4 justify-between"
              >
                <div
                  class="text-lg font-medium leading-5 text-slate-700 dark:text-slate-100 hover:underline-offset-4"
                >
                  {{ account.name }}
                </div>
                <div
                  class="text-xs font-medium flex items-center lowercase text-slate-500 dark:text-slate-500 hover:underline-offset-4"
                >
                  {{
                    account.custom_role_id
                      ? account.custom_role.name
                      : account.role
                  }}
                </div>

                <fluent-icon
                  v-show="account.id === accountId"
                  class="text-slate-800 dark:text-slate-100"
                  icon="checkmark-circle"
                  type="solid"
                  size="24"
                />
              </div>
            </span>
          </button>
        </div>

        <div
          v-if="filteredOtherInstances.length > 0"
          class="mt-4 pt-4 border-t border-slate-75 dark:border-slate-700"
        >
          <h4
            class="px-3 mb-2 text-xs font-medium tracking-wide uppercase text-slate-500 dark:text-slate-400"
          >
            {{ $t('SIDEBAR_ITEMS.OTHER_WORKSPACES') }}
          </h4>
          <div
            v-for="instance in filteredOtherInstances"
            :key="`instance-${instance.accountId}-${instance.instanceHost}`"
            class="pt-0 pb-0"
          >
            <button
              class="flex items-center justify-between w-full px-3 py-2 rounded-lg cursor-pointer expanded clear link hover:underline hover:bg-slate-25 dark:hover:bg-slate-900"
              @click="onChangeInstance(instance)"
            >
              <span class="w-full cursor-pointer">
                <div class="flex flex-col text-left rtl:text-right">
                  <div
                    class="text-base font-medium leading-5 text-slate-700 dark:text-slate-100"
                  >
                    {{ instance.accountName }}
                  </div>
                  <div
                    class="mt-0.5 text-xs text-slate-500 dark:text-slate-500"
                  >
                    {{ instance.instanceHost }}
                  </div>
                </div>
              </span>
              <fluent-icon
                icon="arrow-right"
                class="text-slate-400"
                size="18"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="globalConfig.createNewAccountFromDashboard"
      class="flex items-center justify-end gap-2 px-8 pt-4 pb-8"
    >
      <button
        class="w-full button success large expanded nice"
        @click="$emit('showCreateAccountModal')"
      >
        {{ $t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
      </button>
    </div>
  </woot-modal>
</template>
